"use client";

import { Instagram, Loader, Mail, Rss, Video } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuList() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="flex flex-col gap-3">
        <li className="h-5 w-24 bg-muted/40 animate-pulse" />
        <li className="h-5 w-20 bg-muted/40 animate-pulse" />
        <li className="h-5 w-28 bg-muted/40 animate-pulse" />
        <li className="h-5 w-16 bg-muted/40 animate-pulse" />
      </ul>;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li data-index={i} key={item.id}>
          <Link to={item.href || "#"} className="text-sm font-default text-foreground/70 hover:text-primary transition-colors duration-200">
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-l-2 border-primary pl-4 py-3">
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-foreground/70 mb-2">
          — REQUEST RECEIVED
        </p>
        <p className="text-sm font-default text-foreground/90 mb-3">{successMessage}</p>
        <Button type="button" onClick={resetForm} variant="link" className="h-auto p-0 text-xs font-mono uppercase tracking-[0.18em] text-primary hover:text-primary/80">
          Send Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <FormField control={control} name="first_name" render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input placeholder="Name (optional)" {...field} className="bg-transparent border-0 border-b border-border px-0 h-10 text-sm font-default text-foreground placeholder:text-foreground/40 focus-visible:ring-0 focus-visible:border-primary" />
            </FormControl>
            <FormMessage className="text-xs font-mono" />
          </FormItem>} />
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <div className="relative">
                <Input type="email" placeholder="email@address.com" {...field} className="bg-transparent border-0 border-b border-border px-0 pr-6 h-10 text-sm font-default text-foreground placeholder:text-foreground/40 focus-visible:ring-0 focus-visible:border-primary" />
                <span className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-primary opacity-0 peer-focus-visible:opacity-100" />
              </div>
            </FormControl>
            <FormMessage className="text-xs font-mono" />
          </FormItem>} />

      {submitError && <p className="text-xs font-mono uppercase tracking-[0.16em] text-destructive">{submitError}</p>}

      <Button type="submit" disabled={isSubmitting} variant="ghost" className="self-start h-auto p-0 mt-2 text-xs font-mono uppercase tracking-[0.18em] text-primary hover:bg-transparent hover:text-primary/80 group">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-3" />
            Sending
          </> : <>
            <span className="w-1.5 h-1.5 bg-primary mr-2 transition-transform duration-200 group-hover:translate-x-1" />
            Subscribe
          </>}
      </Button>
    </Form>;
}
export default function FooterEssentials() {
  const socials = [{
    name: "Instagram",
    icon: Instagram,
    href: "#"
  }, {
    name: "Vimeo",
    icon: Video,
    href: "#"
  }, {
    name: "Mail",
    icon: Mail,
    href: "#"
  }, {
    name: "Blog",
    icon: Rss,
    href: "#"
  }];
  const legalLinks = [{
    label: "Privacy",
    to: "#"
  }, {
    label: "Terms",
    to: "#"
  }, {
    label: "Accessibility",
    to: "#"
  }];
  return <section data-section-id="1263" id="footer-essentials" className="bg-background text-foreground py-20 md:py-24 px-8 md:px-16 font-default">
      <div className="max-w-[1320px] mx-auto">
        {}
        <div className="flex items-center gap-3 mb-16">
          <span className="font-mono text-xs uppercase tracking-[0.22em] text-foreground/60">
            S&S / 07
          </span>
          <span className="h-px flex-1 bg-border" />
          <span className="font-mono text-xs uppercase tracking-[0.22em] text-foreground/60">
            CLEANING
          </span>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {}
          <div className="md:col-span-5 flex flex-col gap-8">
            <Link to="/" className="inline-flex items-center gap-3 group">
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-primary" aria-hidden="true">
                <circle cx="18" cy="12" r="5" stroke="currentColor" strokeWidth="1.25" />
                <path d="M8 28 Q 12 22, 18 22 Q 24 22, 28 28" stroke="currentColor" strokeWidth="1.25" fill="none" strokeLinecap="square" />
                <path d="M14 17 Q 18 19, 22 17" stroke="currentColor" strokeWidth="1.25" fill="none" strokeLinecap="square" />
              </svg>
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-foreground/80 group-hover:text-primary transition-colors">
                SPARKLE & SHINE
              </span>
            </Link>

            <p className="font-serif italic font-light text-xl md:text-2xl leading-snug text-foreground max-w-[280px]">
              Restoring clarity to your
              <br />
              most important space.
            </p>

            <div className="flex flex-col gap-2">
              <span className="font-serif italic text-lg text-foreground/90" style={{
              fontFamily: "var(--font-serif)"
            }}>
                — The Cleaning Team
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.18em] text-foreground/50">
                EST. 2025 / PRISTINE
              </span>
            </div>
          </div>

          {}
          <div className="md:col-span-3 flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-primary" />
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-foreground">
                SERVICES
              </span>
            </div>
            <MenuProvider menu_id="31" className="footer-menu-services">
              <FooterMenuList />
            </MenuProvider>
          </div>

          {}
          <div className="md:col-span-2 flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-primary" />
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-foreground">
                COMPANY
              </span>
            </div>
            <MenuProvider menu_id="32" className="footer-menu-company">
              <FooterMenuList />
            </MenuProvider>
          </div>

          {}
          <div className="md:col-span-2 flex flex-col gap-8">
            <div className="flex items-center gap-4">
              {socials.map((social, i) => {
              const Icon = social.icon;
              return <Link key={social.name} data-index={i} to={social.href} aria-label={social.name} className="text-foreground/70 hover:text-primary transition-colors duration-200">
                    <Icon strokeWidth={1.25} className="size-5" />
                  </Link>;
            })}
            </div>

            <div className="flex flex-col gap-3">
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-foreground/60">
                CLEANING UPDATES
              </span>
              <FormProvider formKey="footer_contact_form_home" formId="footer_contact_form_home" sectionName="footer" defaultValues={{
              first_name: "",
              email: ""
            }} validationRules={{
              first_name: {
                maxLength: 50
              },
              email: {
                required: true,
                pattern: "email"
              }
            }} submitText="Subscribe" successMessage="Welcome to a cleaner home. Updates coming soon." errorMessage="Something went wrong. Please try again.">
                <NewsletterForm />
              </FormProvider>
            </div>
          </div>
        </div>

        {}
        <div className="mt-20 flex items-center gap-6">
          <span className="h-px flex-1 bg-border" />
          <span className="font-mono text-xs uppercase tracking-[0.22em] text-foreground/50">
            — BUILT FOR PRISTINE LIVING
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>

        {}
        <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-foreground/50">
            © 2025 SPARKLE & SHINE MAIDS. ALL RIGHTS RESERVED.
          </span>
          <div className="flex items-center gap-4">
            {legalLinks.map((link, i) => <div key={link.label} data-index={i} className="flex items-center gap-4">
                <Link to={link.to} className="font-mono text-[11px] uppercase tracking-[0.22em] text-foreground/60 hover:text-foreground transition-colors">
                  {link.label}
                </Link>
                {i < legalLinks.length - 1 && <span className="w-1 h-1 bg-primary" />}
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}